import { memo, useEffect, useMemo, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	FormHelperText,
	FormLabel,
	Grid,
	Theme,
	Typography,
	makeStyles
} from "@material-ui/core";

import {
	CheckboxWithLabelField,
	CurrencyField,
	MuiAutocompleteField,
	TextField
} from "@ploy-ui/form-fields";
import { useCallback } from "react";
import { isArray } from "lodash";
import {
	FormHelperTextContainerWrapper,
	HelperTextIconButton,
	InputFieldProps,
	LabelComponent
} from "../..";
import { defineMessages, useIntl } from "react-intl";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import { v4 as uuid } from "uuid";
import { Formik, Field as FormikField, useFormikContext } from "formik";
import { SectionProps } from "../../section";
import { addRegisteredSectionLayout } from "../../section/sectionLayoutDescriptions";
import {
	fieldOptionListVariableName,
	useVariableData
} from "@ploy-lib/calculation";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import {
	ErrorDisplay,
	ApplicationDebt,
	ApplicationDebtSource
} from "@ploy-lib/types";

const DEBTS_FIELD_NAME = "Calculator.Debts";

export interface ExtendedApplicationDebt extends ApplicationDebt {
	_frontendOnlyId?: string;
	_isNewEntry?: boolean;
}

const useStyles = makeStyles(
	(theme: Theme) => ({
		root: {},
		listItem: {
			padding: theme.spacing(2)
		},
		expanded: {
			backgroundColor: theme.palette.primary.light,
			border: `1px solid ${theme.palette.secondary.light}`
		},
		childNumber: {
			paddingBottom: theme.spacing(2),
			paddingTop: theme.spacing(2)
		},
		header: {
			fontWeight: 700,
			paddingBottom: theme.spacing(4),
			padding: theme.spacing(0.5)
		},
		debtInternalLabel: {
			marginBottom: theme.spacing(2),
			paddingLeft: "0px !important" // Override Grid item xs' left padding
		},
		limitedDebtElementContainer: {
			marginTop: theme.spacing(4)
		},
		suppliedDebtsWrapper: {
			border: "1px solid #757575",
			borderRadius: 4,
			backgroundColor: "#fff",
			padding: theme.spacing(2)
		},
		suppliedDebtsElement: {
			marginBottom: theme.spacing(1),
			"&:last-child": {
				marginBottom: 0
			}
		},
		lenderLabel: {
			overflowWrap: "anywhere",
			fontWeight: "bold"
		},
		boldText: {
			fontWeight: "bold",
			fontSize: 16
		},
		addButton: {
			borderRadius: 4,
			justifyContent: "start",
			padding: "8px 24px",
			border: `1px solid ${theme.palette.common.black}`,
			fontSize: 16,
			background: "#fff",
			marginTop: theme.spacing(1)
		},
		addButtonPlusIcon: {
			marginRight: 8
		},
		expandMoreIcon: {
			color: theme.palette.primary.main
		},
		spacingAboveField: {
			paddingTop: 12
		},
		innerHelperTextContainer: {
			paddingLeft: 5
		}
	}),
	{ name: "DployLimitedDebtField" }
);

const messages = defineMessages({
	loanType: {
		id: "template-form.field.limitedDebt.loanType",
		defaultMessage: "Loan type"
	},
	amount: {
		id: "template-form.field.limitedDebt.amount",
		defaultMessage: "Amount"
	},
	delete: {
		id: "template-form.field.limitedDebt.delete",
		defaultMessage: "Delete"
	},
	update: {
		id: "template-form.field.limitedDebt.update",
		defaultMessage: "Update"
	},
	save: {
		id: "template-form.field.limitedDebt.save",
		defaultMessage: "Save"
	},
	rememberToSave: {
		id: "template-form.field.limitedDebt.rememberToSave",
		defaultMessage: "Click the button to save the changes"
	},
	addMore: {
		id: "template-form.field.limitedDebt.addMore",
		defaultMessage: "Add more"
	},
	lender: {
		id: "template-form.field.limitedDebt.lender",
		defaultMessage: "Lender"
	},
	loanNumber: {
		id: "template-form.field.limitedDebt.loanNumber",
		defaultMessage: "Loan number"
	},
	newLoan: {
		id: "template-form.field.limitedDebt.newLoan",
		defaultMessage: "New loan"
	},
	willBeFulfilled: {
		id: "template-form.field.limitedDebt.willBeFulfilled",
		defaultMessage: "Will be fulfilled after sale"
	},
	willBeFulfilledHelperText: {
		id: "template-form.field.limitedDebt.willBeFulfilledHelperText",
		defaultMessage:
			"Check if the loan will be fulfilled when selling your existing home"
	},
	missingLenderHeader: {
		id: "template-form.field.limitedDebt.missingLenderHeader",
		defaultMessage: "Supply lender"
	},
	loanTypeIsRequired: {
		id: "template-form.field.limitedDebt.loanTypeIsRequired",
		defaultMessage: "Loan type is required"
	},
	loanNumberIsRequired: {
		id: "template-form.field.limitedDebt.loanNumberIsRequired",
		defaultMessage: "Loan number is required"
	},
	lenderIsRequired: {
		id: "template-form.field.limitedDebt.lenderIsRequired",
		defaultMessage: "Lender is required"
	},
	amountIsRequired: {
		id: "template-form.field.limitedDebt.amountIsRequired",
		defaultMessage: "Amount is required and must be larger than zero"
	},
	debtRegistryHelperText: {
		id: "template-form.field.limitedDebt.debtRegistryHelperText",
		defaultMessage: "Retrieved debt"
	}
});

const debtSourceMessages = defineMessages<ApplicationDebtSource>({
	[ApplicationDebtSource.NotSet]: {
		id: "template-form.field.limitedDebt.debtSource.notSet",
		defaultMessage: "Not set"
	},
	[ApplicationDebtSource.Applicant]: {
		id: "template-form.field.limitedDebt.debtSource.applicant",
		defaultMessage: "Applicant"
	},
	[ApplicationDebtSource.DebtRegistry]: {
		id: "template-form.field.limitedDebt.debtSource.debtRegistry",
		defaultMessage: "Debt registry"
	},
	[ApplicationDebtSource.Taxdata]: {
		id: "template-form.field.limitedDebt.debtSource.taxdata",
		defaultMessage: "Tax data"
	},
	[ApplicationDebtSource.CustomerEngagement]: {
		id: "template-form.field.limitedDebt.debtSource.customerEngagement",
		defaultMessage: "Customer engagement" // Override in tenant-specific translations file for correct backoffice label
	}
});

interface LimitedDebtProps extends Pick<SectionProps, "fields"> {
	canEditEverything?: boolean;
	canEditAmount?: boolean;
	canEditLoanType?: boolean;
	canEditWillBeFulfilled?: boolean;
	data: Readonly<ExtendedApplicationDebt>;
	onUpdate?: (data: ExtendedApplicationDebt) => void;
	onDelete?: (data: ExtendedApplicationDebt) => void;
	loanTypesValues: string[];
	loanTypesDict: { [key: string]: string };
}

const fieldsToTouch = [
	"loanType",
	"lender",
	"amount",
	"loanNumber"
] as (keyof ExtendedApplicationDebt)[];

const fieldsToTouchDictionary = Object.fromEntries(
	fieldsToTouch.map(key => [key, true])
);

const LimitedDebtElement = (props: LimitedDebtProps) => {
	const classes = useStyles();
	const intl = useIntl();

	const [expanded, setExpanded] = useState(false);

	const { loanTypesDict, loanTypesValues } = props;

	const applicationDebtSchema = useMemo(
		() => makeApplicationDebtSchema(intl, loanTypesValues),
		[intl, loanTypesValues]
	);

	// On initial render, if data doesn't have a loanType: Expand the Accordion
	useEffect(() => {
		if (!props.data.loanType) setExpanded(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { touched, errors, isValid } = useFormikContext();
	const isFieldTouched = !!touched["Calculator"]?.["Debts"];

	const isExpandableAndEditableDebtBox = props.onUpdate !== undefined;

	const { _isNewEntry } = props.data;
	const isExpanded = _isNewEntry || expanded;

	return (
		<Formik
			initialValues={props.data}
			enableReinitialize
			validationSchema={applicationDebtSchema}
			onSubmit={(debtToUpdate, helpers) => {
				props.onUpdate?.(debtToUpdate);
				setExpanded(false);
				return Promise.resolve(debtToUpdate);
			}}
		>
			{formikProps => (
				<Accordion
					expanded={isExpanded}
					onChange={async (_, updatedExpanded) => {
						if (!isExpandableAndEditableDebtBox) return;
						const errorMessages = await formikProps.validateForm();
						// validateForm() doesn't set formikProps.isValid => false, or formikProps.isDirty => true..
						// Must instead take a look at validationresult to prevent closing of Accordion

						if (Object.keys(errorMessages).length > 0 || !formikProps.isValid) {
							// Prevent closing if there are errors
							formikProps.setTouched(fieldsToTouchDictionary, true);
							return;
						}
						if (!formikProps.dirty) setExpanded(updatedExpanded);
					}}
					className={clsx(classes.listItem, {
						[classes.expanded]: isExpanded
					})}
				>
					<AccordionSummary
						style={
							isExpandableAndEditableDebtBox ? undefined : { cursor: "default" }
						}
						expandIcon={
							isExpandableAndEditableDebtBox ? (
								<ExpandMoreIcon className={classes.expandMoreIcon} />
							) : null
						}
					>
						{isExpanded ? (
							<Typography className={classes.lenderLabel}>
								{props.data.lender || intl.formatMessage(messages.newLoan)}
							</Typography>
						) : (
							<Grid container justifyContent="space-between">
								<Typography className={classes.boldText}>
									{loanTypesDict[formikProps.values.loanType] ??
										formikProps.values.loanType}
								</Typography>
								<Typography className={classes.boldText}>
									{intl.formatNumber(formikProps.values.amount, {
										format: "currency",
										minimumFractionDigits: 0
									})}
								</Typography>
							</Grid>
						)}
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={4} direction="column">
							{props.canEditEverything ? (
								<Grid item>
									<DebtField
										fieldName="lender"
										component={TextField}
										isCalcFieldTouched={isFieldTouched}
										className={classes.spacingAboveField}
									/>
								</Grid>
							) : (
								<Grid item style={{ paddingTop: 8 }}>
									<Typography
										variant="body2"
										className={classes.lenderLabel}
										gutterBottom
									>
										{props.data.loanNumber}
									</Typography>
								</Grid>
							)}
							<Grid item>
								<DebtField
									fieldName="loanType"
									component={props => (
										<MuiAutocompleteField
											{...props}
											onSelectItem={item => {
												props.form.setFieldValue(
													"willBeFulfilled",
													false,
													false
												);
											}}
										/>
									)}
									isCalcFieldTouched={isFieldTouched}
									restProps={{
										items: loanTypesValues,
										disableClearable: true,
										getItemLabel: (lt: string) => loanTypesDict[lt],
										getItemValue: (lt: string) =>
											loanTypesDict[lt] != null ? lt : undefined
									}}
								/>
							</Grid>
							<Grid item>
								<DebtField
									fieldName="amount"
									component={CurrencyField}
									isCalcFieldTouched={isFieldTouched}
									restProps={{
										minimumFractionDigits: 0,
										emptyEqualsZero: false
									}}
								/>
							</Grid>
							{props.canEditEverything ? (
								<Grid item>
									<DebtField
										fieldName="loanNumber"
										component={TextField}
										isCalcFieldTouched={isFieldTouched}
									/>
								</Grid>
							) : null}
							{(props.canEditEverything || props.canEditWillBeFulfilled) &&
							props.fields?.some(f => f.name === "WillBeFulfilled") &&
							formikProps.values.loanType === "mortgage" ? (
								<Grid item>
									<DebtField
										fieldName="willBeFulfilled"
										hasHelperText={true}
										component={CheckboxWithLabelField}
										isCalcFieldTouched={isFieldTouched}
									/>
								</Grid>
							) : null}
							{props.onUpdate != null &&
							(props.canEditAmount ||
								props.canEditLoanType ||
								props.canEditEverything) ? (
								<Grid item container justifyContent="space-between">
									<Button
										disabled={!formikProps.dirty || !formikProps.isValid}
										onClick={() => {
											formikProps.submitForm();
										}}
										variant="contained"
									>
										{_isNewEntry
											? intl.formatMessage(messages.save)
											: intl.formatMessage(messages.update)}
									</Button>
									{props.onDelete != null && props.canEditEverything ? (
										<Button
											onClick={() => props.onDelete?.(props.data)}
											variant="contained"
										>
											{intl.formatMessage(messages.delete)}
										</Button>
									) : null}
								</Grid>
							) : null}
							{isFieldTouched && !isValid && formikProps.dirty && (
								<Grid item>
									<FormHelperText error>
										{intl.formatMessage(messages.rememberToSave)}
									</FormHelperText>
								</Grid>
							)}
						</Grid>
					</AccordionDetails>
				</Accordion>
			)}
		</Formik>
	);
};

interface DebtFieldProps {
	fieldName: keyof ApplicationDebt;
	component: React.ReactNode;
	isCalcFieldTouched: boolean;
	className?: string;
	restProps?: any;
	hasHelperText?: boolean;
}

const DebtField = (props: DebtFieldProps) => {
	const {
		fieldName,
		component,
		isCalcFieldTouched,
		className,
		restProps,
		hasHelperText
	} = props;

	const { validateForm, isValid, setTouched } = useFormikContext();
	const [showHelperText, setShowHelperText] = useState(false);
	const classes = useStyles();

	const intl = useIntl();

	useEffect(() => {
		// This validation is triggered when the user has clicked on the form's "Next-button"
		// Validation on field-input is handled in the parent component.
		const validateAsync = async () => {
			const validationErrors = await validateForm();

			if (Object.entries(validationErrors).length > 0) {
				setTouched(fieldsToTouchDictionary, true); // Touch all debt-fields to display error
			}
		};

		// When user has clicked the form's "Next-button", validate the form:
		if (isCalcFieldTouched) {
			validateAsync();
		}
		// only "isCalcFieldTouched" in deps-array
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCalcFieldTouched]);

	return (
		<div className={className}>
			<FormikField
				name={fieldName}
				component={component}
				label={
					<Grid container alignItems="center">
						<Grid item xs={11}>
							{intl.formatMessage(messages[fieldName])}
						</Grid>
						{hasHelperText ? (
							<Grid item xs={1} className={classes.innerHelperTextContainer}>
								<HelperTextIconButton
									toggleHelperText={() =>
										setShowHelperText(display => !display)
									}
									showHelperText={showHelperText}
								/>
							</Grid>
						) : null}
					</Grid>
				}
				fullWidth
				error={!isValid}
				errorDisplay={ErrorDisplay.Touched}
				{...restProps}
			/>
			{hasHelperText ? (
				<Grid item container xs={12}>
					{showHelperText ? (
						<FormHelperTextContainerWrapper
							helperText={intl.formatMessage(
								messages[`${fieldName}HelperText`]
							)}
						/>
					) : null}
				</Grid>
			) : null}
		</div>
	);
};

interface LimitedDebtsInternalProps extends Pick<SectionProps, "fields"> {
	label?: string | JSX.Element;
	debtList: Readonly<ExtendedApplicationDebt[]>;
	onAdd?: () => void;
	onUpdate?: (debt: ExtendedApplicationDebt) => void;
	onDelete?: (debt: ExtendedApplicationDebt) => void;
	className?: string;
}

const LimitedDebtsInternal = memo((props: LimitedDebtsInternalProps) => {
	const intl = useIntl();
	const classes = useStyles();

	const { debtList } = props;

	type KeyValue = {
		key: string;
		value: string;
	};

	const loanTypesRaw = (
		useVariableData(
			"Calculator",
			fieldOptionListVariableName("LoanTypeOptionHelper")
		).value as KeyValue[] | undefined
	)?.filter(l => l.key !== "CustomerEngagement");

	const [loanTypesDict, loanTypesValues] = useMemo(
		() => [
			Object.fromEntries(
				(loanTypesRaw ?? []).map(lt => [lt.key, lt.value] as const)
			),
			(loanTypesRaw ?? []).map(l => l.key)
		],
		[loanTypesRaw]
	);

	return (
		<Grid container className={props.className}>
			{props.label ? (
				<Grid item xs={12} className={classes.debtInternalLabel}>
					<FormLabel component="legend">{props.label}</FormLabel>
				</Grid>
			) : null}
			{debtList.length > 0 ? (
				<Grid container spacing={1}>
					{debtList.map(data => (
						<Grid
							item
							xs={12}
							key={data._frontendOnlyId ?? data.applicationDebtId}
						>
							<LimitedDebtElement
								data={data}
								onUpdate={props.onUpdate}
								onDelete={props.onDelete}
								canEditEverything={
									data.source === ApplicationDebtSource.Applicant
								}
								canEditAmount={data.source === ApplicationDebtSource.Taxdata}
								canEditLoanType={data.source === ApplicationDebtSource.Taxdata}
								canEditWillBeFulfilled={
									data.source === ApplicationDebtSource.Taxdata
								}
								loanTypesDict={loanTypesDict}
								loanTypesValues={loanTypesValues}
								fields={props.fields}
							/>
						</Grid>
					))}
				</Grid>
			) : null}
			{props.onAdd != null ? (
				<Grid item xs={12}>
					<Button
						onClick={props.onAdd}
						variant="outlined"
						className={classes.addButton}
						fullWidth
					>
						<AddCircleOutlineIcon className={classes.addButtonPlusIcon} />
						{intl.formatMessage(messages.addMore)}
					</Button>
				</Grid>
			) : null}
		</Grid>
	);
});

function makeApplicationDebtSchema(
	intl,
	loanTypesValues: string[]
): Yup.ObjectSchema<
	{ loanType: string; lender: string; amount: number; loanNumber: string },
	Yup.AnyObject,
	{
		loanType: undefined;
		lender: undefined;
		amount: undefined;
		loanNumber: undefined;
	},
	""
> {
	return Yup.object({
		loanType: Yup.string()
			.required(intl.formatMessage(messages.loanTypeIsRequired))
			.oneOf(loanTypesValues),
		lender: Yup.string()
			.required(intl.formatMessage(messages.lenderIsRequired))
			.min(3, intl.formatMessage(messages.lenderIsRequired))
			.max(50, intl.formatMessage(messages.lenderIsRequired)),
		amount: Yup.number()
			.required(intl.formatMessage(messages.amountIsRequired))
			.typeError(intl.formatMessage(messages.amountIsRequired)) // When input-field is empty (undefined => NaN)
			.min(0, intl.formatMessage(messages.amountIsRequired))
			.max(99999999, intl.formatMessage(messages.amountIsRequired)),
		loanNumber: Yup.string()
			.required(intl.formatMessage(messages.loanNumberIsRequired))
			.matches(/^\d{11}$/, intl.formatMessage(messages.loanNumberIsRequired))
	});
}

export function debtExistencePredicate(
	itemFromCollection: ExtendedApplicationDebt,
	needle: ExtendedApplicationDebt
) {
	return (
		(needle._frontendOnlyId != null &&
			needle._frontendOnlyId === itemFromCollection._frontendOnlyId) ||
		(needle._frontendOnlyId == null &&
			needle.applicationDebtId === itemFromCollection.applicationDebtId)
	);
}

export function ensureCollectionHasIds(items: any): ExtendedApplicationDebt[] {
	return (
		!isArray(items) ? [] : (items as ExtendedApplicationDebt[]) || []
	).map(debt =>
		// Just in case application / calc rules are reloaded but debt are not saved to database. May cause rerenders.
		(debt.applicationDebtId == null || debt.applicationDebtId === 0) &&
		!debt._frontendOnlyId
			? { ...debt, _frontendOnlyId: uuid() }
			: debt
	);
}

interface LimitedDebtsFieldProps
	extends InputFieldProps,
		Pick<SectionProps, "fields"> {}

const LimitedDebtsField = memo((props: LimitedDebtsFieldProps) => {
	const intl = useIntl();
	const classes = useStyles();

	const debtList = useMemo(
		() => ensureCollectionHasIds(props.field.value),
		[props.field.value]
	);

	const maybeUntouchCalcField = useCallback(
		(debts: ExtendedApplicationDebt[]) => {
			if (debts.some(x => x._isNewEntry)) {
				// Any new, unsaved, debts?
				// If so, don't untouch the Debts field (keep it touched)
				return;
			}
			// No new, unsaved entries - untouch the Debts field (enabling the form's Next-button)
			props.form.setFieldTouched(DEBTS_FIELD_NAME, false);
		},
		[props.form]
	);

	const addItem = useCallback(() => {
		const updatedList = [
			...debtList,
			{
				lender: "",
				amount: NaN,
				loanType: "",
				source: ApplicationDebtSource.Applicant,
				applicationDebtId: undefined,
				loanNumber: "",
				termAmount: 0,
				refinance: false,
				comment: "",
				_frontendOnlyId: uuid(),
				_isNewEntry: true
			} as ExtendedApplicationDebt
		];
		props.form.setFieldValue(props.field.name, updatedList);
	}, [debtList, props.field.name, props.form]);

	const updateItem = useCallback(
		(updatedData: ExtendedApplicationDebt) => {
			const updatedList = debtList.map(debt => {
				const entry = debtExistencePredicate(debt, updatedData)
					? updatedData
					: debt;
				delete entry._isNewEntry;
				return entry;
			});
			props.form.setFieldValue(props.field.name, updatedList);
			maybeUntouchCalcField(updatedList);
		},
		[debtList, props.field.name, props.form, maybeUntouchCalcField]
	);

	const deleteItem = useCallback(
		(debtToDelete: ExtendedApplicationDebt) => {
			const updatedList = debtList.filter(
				debt => !debtExistencePredicate(debt, debtToDelete)
			);
			props.form.setFieldValue(props.field.name, updatedList);
			maybeUntouchCalcField(updatedList);
		},
		[debtList, props.field.name, props.form, maybeUntouchCalcField]
	);

	const userSuppliedDebts = useMemo(
		() =>
			debtList.filter(debt => debt.source === ApplicationDebtSource.Applicant),
		[debtList]
	);
	const debtRegistryDebts = useMemo(
		() =>
			debtList.filter(
				debt => debt.source === ApplicationDebtSource.DebtRegistry
			),
		[debtList]
	);
	const taxRegistryDebts = useMemo(
		() =>
			debtList.filter(debt => debt.source === ApplicationDebtSource.Taxdata),
		[debtList]
	);
	const backofficeEngagementDebts = useMemo(
		() =>
			debtList.filter(
				debt => debt.source === ApplicationDebtSource.CustomerEngagement
			),
		[debtList]
	);

	const [showDebtRegistryHelperText, setShowHelperText] = useState(false);

	const toggleDebtRegistryHelperText = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			setShowHelperText(show => !show);
			e.stopPropagation();
		},
		[]
	);

	return (
		<Grid
			container
			spacing={1}
			className={clsx(classes.root, props.className)}
			onClick={props.onClick}
		>
			{props.label ? (
				<Grid item xs={12}>
					<FormLabel component="legend">{props.label}</FormLabel>
				</Grid>
			) : null}
			{taxRegistryDebts.length > 0 ? (
				<Grid item xs={12}>
					<LimitedDebtsInternal
						fields={props.fields}
						debtList={taxRegistryDebts}
						label={intl.formatMessage(
							debtSourceMessages[ApplicationDebtSource.Taxdata]
						)}
						onUpdate={updateItem}
					/>
				</Grid>
			) : null}
			<Grid item xs={12}>
				<LimitedDebtsInternal
					fields={props.fields}
					debtList={userSuppliedDebts}
					label="" // Shouldn't have a separate title for applicant, set empty string
					onAdd={addItem}
					onUpdate={updateItem}
					onDelete={deleteItem}
				/>
			</Grid>
			{backofficeEngagementDebts.length > 0 ? (
				<Grid item xs={12}>
					<LimitedDebtsInternal
						className={classes.limitedDebtElementContainer}
						fields={props.fields}
						debtList={[
							{
								loanType: "Lån", // They all have the same loanType
								amount: backofficeEngagementDebts.reduce((acc, curr) => {
									return acc + curr.amount;
								}, 0),
								lender: backofficeEngagementDebts[0].lender,
								source: ApplicationDebtSource.CustomerEngagement,
								_frontendOnlyId: backofficeEngagementDebts[0]._frontendOnlyId,
								applicationDebtId:
									backofficeEngagementDebts[0].applicationDebtId,
								comment: "",
								loanNumber: "",
								originalAmount: 0,
								refinance: false,
								refinanceAccount: "",
								refinanceAmount: 0,
								termAmount: 0,
								willBeFulfilled: false
							}
						]}
						label={intl.formatMessage(
							debtSourceMessages[ApplicationDebtSource.CustomerEngagement]
						)}
					/>
				</Grid>
			) : null}
			{debtRegistryDebts.length > 0 ? (
				<Grid item xs={12}>
					<LimitedDebtsInternal
						fields={props.fields}
						className={classes.limitedDebtElementContainer}
						debtList={debtRegistryDebts}
						label={
							<LabelComponent
								labelText={intl.formatMessage(
									debtSourceMessages[ApplicationDebtSource.DebtRegistry]
								)}
								isFirst={true}
								trimmedHelperText={intl.formatMessage(
									messages.debtRegistryHelperText
								)}
								displayHelperTextAboveField={true}
								variant={"outlined"}
								showHelperText={showDebtRegistryHelperText}
								toggleHelperText={toggleDebtRegistryHelperText}
							/>
						}
					/>
				</Grid>
			) : null}
		</Grid>
	);
});

export const LimitedDebtsSection = (sectionProps: SectionProps) => {
	// This section started as a custom field 😅
	return (
		<FormikField
			component={LimitedDebtsField}
			name={DEBTS_FIELD_NAME}
			className={sectionProps.className}
			onClick={sectionProps.onClick}
			literal={sectionProps.literal}
			fields={sectionProps.fields}
		/>
	);
};

// Credit: https://github.com/jaredpalmer/formik/issues/2769#issuecomment-1872382784
// export const FormikAutoSubmit = () => {
// 	/*
// 	  This component is used to automatically submit the form when the form is valid
// 	  and has been changed(dirty).
// 	 */
// 	const { isValid, values, dirty, submitForm } = useFormikContext();

// 	useEffect(() => {
// 		if (isValid && dirty) {
// 			submitForm();
// 			return;
// 		}
// 	}, [isValid, values, dirty, submitForm]);

// 	return null;
// };

addRegisteredSectionLayout({
	displayName: "LimitedDebtsSection",
	name: "LimitedDebtsSection",
	settings: {}
});

LimitedDebtsField.displayName = "LimitedDebtsField";
LimitedDebtsSection.displayName = "LimitedDebtsSection";

// export const EditorDebtFields = identityRecordOfFieldEditorOptions({
// 	LimitedDebtsField: {
// 		editableOptions: {}
// 	}
// });

// export const EditorDebtLiterals = identityRecordOfFieldEditorOptions({
// 	// LimitedDebtsLiteral: {
// 	// 	editableOptions: {}
// 	// }
// });

// export {
// 	LimitedDebtsField
// 	//,LimitedDebtsLiteral
// };
